import type { Origin } from '@elena/shared';

const TOKEN_KEY = 'elena.token';
const DEVICE_KEY = 'elena.device';
const ORIGIN_KEY = 'elena.origin';
const SPEAK_KEY = 'elena.speak';
const WAKE_KEY = 'elena.wake';
const STT_KEY = 'elena.stt';

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // private mode: keep working for this tab only
  }
};

export const session = {
  token: () => read(TOKEN_KEY),
  /** The PC widget opens as #/widget?token=…: keep the token and take it out of the address. */
  adoptTokenFromUrl(): void {
    const m = /[?&]token=([^&]+)/.exec(location.hash);
    if (!m?.[1]) return;
    write(TOKEN_KEY, decodeURIComponent(m[1]));
    const hash = location.hash.replace(/[?&]token=[^&]+/, '');
    history.replaceState(null, '', `${location.pathname}${location.search}${hash}`);
  },
  setToken: (token: string | null) => write(TOKEN_KEY, token),

  deviceId(): string {
    let id = read(DEVICE_KEY);
    if (!id) {
      id = `web-${crypto.randomUUID().slice(0, 12)}`;
      write(DEVICE_KEY, id);
    }
    return id;
  },

  /** Phones default to "phone"; everything else to "web". The user can change it in settings. */
  origin(): Origin {
    const saved = read(ORIGIN_KEY);
    if (saved === 'pc' || saved === 'phone' || saved === 'web') return saved;
    return window.matchMedia('(pointer: coarse)').matches ? 'phone' : 'web';
  },
  setOrigin: (origin: Origin) => write(ORIGIN_KEY, origin),

  speakReplies: () => read(SPEAK_KEY) !== 'off',
  setSpeakReplies: (on: boolean) => write(SPEAK_KEY, on ? 'on' : 'off'),

  wakeWord: () => read(WAKE_KEY) === 'on',
  setWakeWord: (on: boolean) => write(WAKE_KEY, on ? 'on' : 'off'),

  /**
   * Who turns the mic into text on this device: the server's recognizer (Soniox, ElevenLabs or
   * Azure; saved as 'azure' by older versions) or only the browser's own.
   */
  sttEngine: (): 'server' | 'google' => (read(STT_KEY) === 'google' ? 'google' : 'server'),
  setSttEngine: (engine: 'server' | 'google') => write(STT_KEY, engine),
};
